/**
 * engine/input.ts — Unified Input Handling
 *
 * Every tap, click, key press, and pinch passes through here and emerges
 * as clean, coordinate-transformed intent. The rest of the engine doesn't
 * know whether you're on a phone or a desktop — it just sees actions.
 *
 * Three input surfaces, one truth:
 *   - Mouse: hover, click, wheel zoom, right-click context
 *   - Touch: tap (click), long-press (context menu), pinch (zoom)
 *   - Keyboard: arrow movement, Enter for chat, Escape for menus
 *
 * Isometric click detection is non-trivial: we reverse-project the screen
 * point through the camera transform to get tile coordinates. The projection
 * code lives in iso/projection.ts — we call it from here.
 */

import { screenToTile, TilePoint } from "../iso/projection.js";
import { GameState, asCamera } from "./state.js";

// ─── Constants ───────────────────────────────────────────────────────────────

/** Long-press threshold in milliseconds before context menu fires */
const LONG_PRESS_MS = 500;

/** Maximum pixels of movement before a touch is no longer a tap */
const TAP_SLOP_PX = 8;

/** Pinch zoom sensitivity (higher = faster zoom change) */
const PINCH_ZOOM_SENSITIVITY = 0.01;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PointerPosition {
  /** Canvas pixel X */
  x: number;
  /** Canvas pixel Y */
  y: number;
}

/**
 * A tile that was clicked/tapped. Null when the user hasn't clicked yet
 * or the click was consumed this frame. Cleared at the start of each frame
 * so systems only see a click once.
 */
export type ClickedTile = TilePoint | null;

/** Chat input mode */
export interface ChatInput {
  open: boolean;
  value: string;
}

/**
 * The full snapshot of input for this frame. Systems read this, not the
 * raw browser events — which are noisy, async, and already consumed.
 */
export interface InputState {
  /** Current pointer/touch position in canvas pixels */
  pointer: PointerPosition;
  /** Tile under the pointer right now (may be outside grid) */
  hoveredTile: TilePoint | null;
  /** Tile that was clicked/tapped this frame — null if no click */
  clickedTile: ClickedTile;
  /** Whether right-click or long-press was triggered this frame */
  contextMenu: boolean;
  /** Set of currently held keyboard keys */
  keys: Set<string>;
  /** Keys that were pressed (went from up to down) this frame */
  keysJustPressed: Set<string>;
  /** Keys that were released this frame */
  keysJustReleased: Set<string>;
  /** Movement direction from arrow keys (dx: -1|0|1, dy: -1|0|1) */
  moveDir: { dx: number; dy: number };
  /** Chat state */
  chat: ChatInput;
  /** Current pinch zoom delta this frame (0 if no pinch) */
  pinchZoomDelta: number;
  /** Current scroll wheel zoom delta this frame */
  wheelZoomDelta: number;
  /** Raw canvas position of a click this frame (null if no click) */
  clickPosition: PointerPosition | null;
  /** Raw canvas position of a right-click/long-press this frame (null if none) */
  contextPosition: PointerPosition | null;
  /** Whether the primary pointer (mouse button / touch) is currently held down */
  pointerDown: boolean;
}

// ─── Internal Mutable Input Buffer ───────────────────────────────────────────

/**
 * The raw buffer that event listeners write into.
 * At the start of each frame the game loop calls `consumeFrame()` to move
 * pending events into the clean InputState that systems read.
 */
interface InputBuffer {
  pointer: PointerPosition;
  pendingClick: PointerPosition | null;
  pendingContext: PointerPosition | null;
  keys: Set<string>;
  justPressed: Set<string>;
  justReleased: Set<string>;
  // Touch tracking
  touches: Map<number, { x: number; y: number; startX: number; startY: number; startTime: number }>;
  longPressTimer: ReturnType<typeof setTimeout> | null;
  longPressTriggered: boolean;
  pinchStartDistance: number;
  pinchCurrentDistance: number;
  pinchActive: boolean;
  // Primary pointer down state
  pointerDown: boolean;
  // Mouse wheel
  wheelDelta: number;
}

// ─── InputSystem ─────────────────────────────────────────────────────────────

/**
 * The input system. Create once, attach to a canvas, update each frame.
 * Designed to be owned by the game loop.
 */
export class InputSystem {
  private canvas: HTMLCanvasElement;
  private chatInputEl: HTMLInputElement | null;
  private buffer: InputBuffer;
  private _state: InputState;
  /** When false, Enter won't auto-open the chat bar. */
  chatEnabled = true;

  // Bound event handlers — stored so removeEventListener can find the same reference
  private _onMouseMove: (e: MouseEvent) => void;
  private _onMouseDown: (e: MouseEvent) => void;
  private _onMouseUp: (e: MouseEvent) => void;
  private _onContextMenu: (e: MouseEvent) => void;
  private _onWheel: (e: WheelEvent) => void;
  private _onTouchStart: (e: TouchEvent) => void;
  private _onTouchMove: (e: TouchEvent) => void;
  private _onTouchEnd: (e: TouchEvent) => void;
  private _onTouchCancel: (e: TouchEvent) => void;
  private _onKeyDown: (e: KeyboardEvent) => void;
  private _onKeyUp: (e: KeyboardEvent) => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.chatInputEl = document.getElementById("chat-input") as HTMLInputElement | null;

    this.buffer = {
      pointer: { x: 0, y: 0 },
      pendingClick: null,
      pendingContext: null,
      keys: new Set(),
      justPressed: new Set(),
      justReleased: new Set(),
      touches: new Map(),
      longPressTimer: null,
      longPressTriggered: false,
      pointerDown: false,
      pinchStartDistance: 0,
      pinchCurrentDistance: 0,
      pinchActive: false,
      wheelDelta: 0,
    };

    this._state = {
      pointer: { x: 0, y: 0 },
      hoveredTile: null,
      clickedTile: null,
      contextMenu: false,
      keys: new Set(),
      keysJustPressed: new Set(),
      keysJustReleased: new Set(),
      moveDir: { dx: 0, dy: 0 },
      chat: { open: false, value: "" },
      pinchZoomDelta: 0,
      wheelZoomDelta: 0,
      clickPosition: null,
      contextPosition: null,
      pointerDown: false,
    };

    // Bind handlers once and store references for clean removal
    this._onMouseMove = this.onMouseMove.bind(this);
    this._onMouseDown = this.onMouseDown.bind(this);
    this._onMouseUp = this.onMouseUp.bind(this);
    this._onContextMenu = this.onContextMenu.bind(this);
    this._onWheel = this.onWheel.bind(this);
    this._onTouchStart = this.onTouchStart.bind(this);
    this._onTouchMove = this.onTouchMove.bind(this);
    this._onTouchEnd = this.onTouchEnd.bind(this);
    this._onTouchCancel = this.onTouchCancel.bind(this);
    this._onKeyDown = this.onKeyDown.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);

    this.attachListeners();
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /** Read-only view of this frame's input. */
  get state(): InputState {
    return this._state;
  }

  /**
   * Called at the top of each frame by the game loop.
   *
   * Flushes the buffer into _state so all systems see the same input
   * snapshot for the entire frame. Clears one-shot events (clicks, just-pressed)
   * so they don't repeat.
   */
  update(gameState: GameState): void {
    const cam = asCamera(gameState);

    // Hover tile — always from current pointer
    let hoveredTile: TilePoint | null = null;
    try {
      const tile = screenToTile(this.buffer.pointer.x, this.buffer.pointer.y, cam);
      // Clamp to room bounds if we have a room
      if (gameState.currentRoom) {
        const { width, height } = gameState.currentRoom;
        if (tile.tx >= 0 && tile.tx < width && tile.ty >= 0 && tile.ty < height) {
          hoveredTile = tile;
        }
      } else {
        hoveredTile = tile;
      }
    } catch {
      // off-screen — no hover
    }

    // Clicked tile — from pending click, if any
    let clickedTile: ClickedTile = null;
    const clickPosition: PointerPosition | null = this.buffer.pendingClick
      ? { ...this.buffer.pendingClick }
      : null;
    if (this.buffer.pendingClick) {
      try {
        clickedTile = screenToTile(
          this.buffer.pendingClick.x,
          this.buffer.pendingClick.y,
          cam
        );
      } catch {
        clickedTile = null;
      }
      this.buffer.pendingClick = null;
    }

    // Pinch zoom delta this frame
    let pinchZoomDelta = 0;
    if (this.buffer.pinchActive && this.buffer.pinchStartDistance > 0) {
      const ratio = this.buffer.pinchCurrentDistance / this.buffer.pinchStartDistance;
      pinchZoomDelta = (ratio - 1) * PINCH_ZOOM_SENSITIVITY * 60; // normalise to per-frame
      this.buffer.pinchStartDistance = this.buffer.pinchCurrentDistance;
    }

    // Wheel zoom delta
    const wheelDelta = this.buffer.wheelDelta;
    this.buffer.wheelDelta = 0;

    // Movement direction from arrow keys / WASD
    const keys = this.buffer.keys;
    const dx =
      (keys.has("ArrowRight") || keys.has("d") || keys.has("D") ? 1 : 0) -
      (keys.has("ArrowLeft")  || keys.has("a") || keys.has("A") ? 1 : 0);
    const dy =
      (keys.has("ArrowDown")  || keys.has("s") || keys.has("S") ? 1 : 0) -
      (keys.has("ArrowUp")    || keys.has("w") || keys.has("W") ? 1 : 0);

    // Chat state mirrors the DOM input element
    const chatOpen = this.chatInputEl?.style.display !== "none" && this._state.chat.open;

    const contextPosition = this.buffer.pendingContext ? { ...this.buffer.pendingContext } : null;

    this._state = {
      pointer: { ...this.buffer.pointer },
      hoveredTile,
      clickedTile,
      contextMenu: !!this.buffer.pendingContext,
      keys: new Set(this.buffer.keys),
      keysJustPressed: new Set(this.buffer.justPressed),
      keysJustReleased: new Set(this.buffer.justReleased),
      moveDir: { dx, dy },
      chat: {
        open: chatOpen,
        value: this.chatInputEl?.value ?? "",
      },
      pinchZoomDelta,
      wheelZoomDelta: wheelDelta,
      clickPosition,
      contextPosition,
      pointerDown: this.buffer.pointerDown,
    };

    // Clear one-shot buffers
    this.buffer.pendingContext = null;
    this.buffer.justPressed.clear();
    this.buffer.justReleased.clear();
  }

  /**
   * Open or close the chat input bar.
   * Handles DOM visibility and focus so the game loop doesn't have to.
   *
   * When deviceRect is provided, the input is repositioned to overlay
   * the device compose bar instead of sitting at the screen bottom.
   */
  toggleChat(open?: boolean, deviceRect?: { x: number; y: number; w: number; h: number }): void {
    const shouldOpen = open !== undefined ? open : !this._state.chat.open;
    this._state.chat.open = shouldOpen;
    if (this.chatInputEl) {
      if (shouldOpen) {
        if (deviceRect) {
          // Position over the device compose bar area.
          // appContentRect is in logical canvas units = CSS pixels = viewport pixels
          // (canvas is styled at logical size; no DPR scaling needed).
          const canvasRect = this.canvas.getBoundingClientRect();
          const vpLeft = canvasRect.left + deviceRect.x;
          const vpTop  = canvasRect.top  + deviceRect.y;
          const vpWidth = deviceRect.w;

          this.chatInputEl.style.position = "fixed";
          this.chatInputEl.style.left = `${vpLeft}px`;
          this.chatInputEl.style.top = `${vpTop}px`;
          this.chatInputEl.style.width = `${vpWidth}px`;
          this.chatInputEl.style.transform = "none";
          this.chatInputEl.style.bottom = "auto";
          this.chatInputEl.style.borderRadius = "4px";
          this.chatInputEl.style.fontSize = "13px";
          this.chatInputEl.style.padding = "5px 10px";
          this.chatInputEl.style.background = "rgba(20,14,8,0.95)";
          this.chatInputEl.style.borderColor = "rgba(255,154,60,0.3)";
          this.chatInputEl.style.color = "#fff5eb";
          this.chatInputEl.placeholder = "Say something to the Aeterna...";
        } else {
          // Standard room chat position (restore defaults)
          this.resetChatInputStyle();
        }
        this.chatInputEl.style.display = "block";
        this.chatInputEl.focus();
      } else {
        this.chatInputEl.style.display = "none";
        this.chatInputEl.value = "";
        this.chatInputEl.blur();
        this._state.chat.value = "";
        // Restore standard styling
        this.resetChatInputStyle();
      }
    }
  }

  /** Reset the chat input element to its default CSS styling */
  private resetChatInputStyle(): void {
    if (!this.chatInputEl) return;
    this.chatInputEl.style.position = "fixed";
    this.chatInputEl.style.bottom = "max(12px, env(safe-area-inset-bottom, 0px))";
    this.chatInputEl.style.left = "50%";
    this.chatInputEl.style.transform = "translateX(-50%)";
    this.chatInputEl.style.width = "min(calc(90vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)), 500px)";
    this.chatInputEl.style.top = "auto";
    this.chatInputEl.style.borderRadius = "16px";
    this.chatInputEl.style.fontSize = "16px";
    this.chatInputEl.style.padding = "8px 14px";
    this.chatInputEl.style.background = "rgba(20, 20, 40, 0.9)";
    this.chatInputEl.style.borderColor = "rgba(100, 100, 140, 0.3)";
    this.chatInputEl.style.color = "#c8c8d4";
    this.chatInputEl.placeholder = "say something...";
  }

  /**
   * Consume the current chat input value and clear the field.
   * Call when the player presses Enter to send their message.
   */
  consumeChatMessage(): string {
    const value = this.chatInputEl?.value?.trim() ?? "";
    this.toggleChat(false);
    return value;
  }

  // ─── Event Listeners ──────────────────────────────────────────────────────

  private attachListeners(): void {
    const canvas = this.canvas;

    // ── Mouse ────────────────────────────────────────────────────────────────
    canvas.addEventListener("mousemove", this._onMouseMove, { passive: true });
    canvas.addEventListener("mousedown", this._onMouseDown, { passive: false });
    window.addEventListener("mouseup", this._onMouseUp, { passive: true });
    canvas.addEventListener("contextmenu", this._onContextMenu, { passive: false });
    canvas.addEventListener("wheel", this._onWheel, { passive: false });

    // ── Touch ─────────────────────────────────────────────────────────────────
    canvas.addEventListener("touchstart",  this._onTouchStart,  { passive: false });
    canvas.addEventListener("touchmove",   this._onTouchMove,   { passive: false });
    canvas.addEventListener("touchend",    this._onTouchEnd,    { passive: false });
    canvas.addEventListener("touchcancel", this._onTouchCancel, { passive: true });

    // ── Keyboard ──────────────────────────────────────────────────────────────
    window.addEventListener("keydown", this._onKeyDown);
    window.addEventListener("keyup",   this._onKeyUp);

    // ── Chat input submit on Enter ────────────────────────────────────────────
    if (this.chatInputEl) {
      this.chatInputEl.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          // Signal to the game loop that a message is ready.
          // The loop calls consumeChatMessage() when it sees this.
          this.buffer.justPressed.add("ChatSubmit");
        }
        if (e.key === "Escape") {
          this.toggleChat(false);
        }
      });
    }
  }

  // ─── Mouse Handlers ────────────────────────────────────────────────────────

  private getCanvasPos(clientX: number, clientY: number): PointerPosition {
    const rect = this.canvas.getBoundingClientRect();
    // Return logical pixel coordinates (NOT buffer coordinates).
    // The ctx is DPR-scaled, so all camera/projection math operates in
    // logical space. rect.left/top already give us the CSS offset.
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }

  private onMouseMove(e: MouseEvent): void {
    this.buffer.pointer = this.getCanvasPos(e.clientX, e.clientY);
  }

  private onMouseDown(e: MouseEvent): void {
    if (e.button === 0) {
      // Left click — only register as tile click if not on UI
      this.buffer.pendingClick = this.getCanvasPos(e.clientX, e.clientY);
      this.buffer.pointerDown = true;
    } else if (e.button === 2) {
      this.buffer.pendingContext = this.getCanvasPos(e.clientX, e.clientY);
    }
  }

  private onMouseUp(e: MouseEvent): void {
    if (e.button === 0) {
      this.buffer.pointerDown = false;
    }
  }

  private onContextMenu(e: MouseEvent): void {
    e.preventDefault();
    this.buffer.pendingContext = this.getCanvasPos(e.clientX, e.clientY);
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    // Normalise across different wheel modes (pixels vs lines vs pages)
    let delta = e.deltaY;
    if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) delta *= 16;
    if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) delta *= 400;
    // Negative delta = scroll up = zoom in
    this.buffer.wheelDelta -= delta * 0.001;
  }

  // ─── Touch Handlers ────────────────────────────────────────────────────────

  private onTouchStart(e: TouchEvent): void {
    e.preventDefault();
    const now = Date.now();

    for (const touch of Array.from(e.changedTouches)) {
      const pos = this.getCanvasPos(touch.clientX, touch.clientY);
      this.buffer.touches.set(touch.identifier, {
        x: pos.x,
        y: pos.y,
        startX: pos.x,
        startY: pos.y,
        startTime: now,
      });
    }

    const touchCount = this.buffer.touches.size;

    if (touchCount === 1) {
      // Single touch — start long-press timer
      this.buffer.longPressTriggered = false;
      if (this.buffer.longPressTimer) clearTimeout(this.buffer.longPressTimer);
      this.buffer.longPressTimer = setTimeout(() => {
        this.buffer.longPressTriggered = true;
        const first2 = Array.from(this.buffer.touches.values())[0];
        this.buffer.pendingContext = first2 ? { x: first2.x, y: first2.y } : { x: 0, y: 0 };
        // Update pointer to touch position for context menu placement
        const first = Array.from(this.buffer.touches.values())[0];
        if (first) this.buffer.pointer = { x: first.x, y: first.y };
      }, LONG_PRESS_MS);
    } else if (touchCount === 2) {
      // Two touches — start pinch
      if (this.buffer.longPressTimer) {
        clearTimeout(this.buffer.longPressTimer);
        this.buffer.longPressTimer = null;
      }
      this.buffer.pinchActive = true;
      this.buffer.pinchStartDistance = this.getPinchDistance();
      this.buffer.pinchCurrentDistance = this.buffer.pinchStartDistance;
    }
  }

  private onTouchMove(e: TouchEvent): void {
    e.preventDefault();

    for (const touch of Array.from(e.changedTouches)) {
      const existing = this.buffer.touches.get(touch.identifier);
      if (!existing) continue;
      const pos = this.getCanvasPos(touch.clientX, touch.clientY);
      existing.x = pos.x;
      existing.y = pos.y;
    }

    if (this.buffer.touches.size === 1) {
      // Move the pointer to follow single touch
      const first = Array.from(this.buffer.touches.values())[0];
      if (first) {
        this.buffer.pointer = { x: first.x, y: first.y };
        // Cancel long press if they moved too far
        const moved = Math.hypot(first.x - first.startX, first.y - first.startY);
        if (moved > TAP_SLOP_PX && this.buffer.longPressTimer) {
          clearTimeout(this.buffer.longPressTimer);
          this.buffer.longPressTimer = null;
        }
      }
    } else if (this.buffer.touches.size === 2) {
      // Update pinch distance
      this.buffer.pinchCurrentDistance = this.getPinchDistance();
    }
  }

  private onTouchEnd(e: TouchEvent): void {
    e.preventDefault();
    const now = Date.now();

    for (const touch of Array.from(e.changedTouches)) {
      const tracked = this.buffer.touches.get(touch.identifier);
      if (!tracked) continue;

      // Was this a tap? (short time, small movement, no long-press triggered)
      const duration = now - tracked.startTime;
      const moved = Math.hypot(tracked.x - tracked.startX, tracked.y - tracked.startY);
      if (
        duration < LONG_PRESS_MS &&
        moved < TAP_SLOP_PX &&
        !this.buffer.longPressTriggered &&
        this.buffer.touches.size === 1
      ) {
        this.buffer.pointer = { x: tracked.x, y: tracked.y };
        this.buffer.pendingClick = { x: tracked.x, y: tracked.y };
      }

      this.buffer.touches.delete(touch.identifier);
    }

    // End pinch when we drop below 2 touches
    if (this.buffer.touches.size < 2) {
      this.buffer.pinchActive = false;
      this.buffer.pinchStartDistance = 0;
    }

    // Cancel long-press timer if all fingers are up
    if (this.buffer.touches.size === 0) {
      if (this.buffer.longPressTimer) {
        clearTimeout(this.buffer.longPressTimer);
        this.buffer.longPressTimer = null;
      }
      this.buffer.longPressTriggered = false;
    }
  }

  private onTouchCancel(e: TouchEvent): void {
    for (const touch of Array.from(e.changedTouches)) {
      this.buffer.touches.delete(touch.identifier);
    }
    this.buffer.pinchActive = false;
    if (this.buffer.longPressTimer) {
      clearTimeout(this.buffer.longPressTimer);
      this.buffer.longPressTimer = null;
    }
  }

  private getPinchDistance(): number {
    const pts = Array.from(this.buffer.touches.values());
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  }

  // ─── Keyboard Handlers ─────────────────────────────────────────────────────

  private onKeyDown(e: KeyboardEvent): void {
    // Don't steal input while chat is focused
    if (document.activeElement === this.chatInputEl) return;

    const key = e.key;

    if (!this.buffer.keys.has(key)) {
      this.buffer.keys.add(key);
      this.buffer.justPressed.add(key);
    }

    // ── Chat toggle ──
    // Signal Enter intent — the game loop handles opening the chat
    // so it can decide positioning (room chat vs device aeterna mode)
    if (key === "Enter" && this.chatEnabled) {
      e.preventDefault();
    }

    // ── Close active panels ──
    if (key === "Escape") {
      // Signal escape — the game loop decides what to close
      // (don't close chat here — it has its own Escape handler)
    }

    // ── Prevent page scroll from arrow keys ──
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(key)) {
      e.preventDefault();
    }
  }

  private onKeyUp(e: KeyboardEvent): void {
    const key = e.key;
    this.buffer.keys.delete(key);
    this.buffer.justReleased.add(key);
    this.buffer.justPressed.delete(key); // can't be both just-pressed and just-released
  }

  // ─── Cleanup ──────────────────────────────────────────────────────────────

  /** Detach all listeners. Call if you're destroying the game instance. */
  destroy(): void {
    const canvas = this.canvas;
    canvas.removeEventListener("mousemove", this._onMouseMove);
    canvas.removeEventListener("mousedown", this._onMouseDown);
    window.removeEventListener("mouseup", this._onMouseUp);
    canvas.removeEventListener("contextmenu", this._onContextMenu);
    canvas.removeEventListener("wheel", this._onWheel);
    canvas.removeEventListener("touchstart",  this._onTouchStart);
    canvas.removeEventListener("touchmove",   this._onTouchMove);
    canvas.removeEventListener("touchend",    this._onTouchEnd);
    canvas.removeEventListener("touchcancel", this._onTouchCancel);
    window.removeEventListener("keydown", this._onKeyDown);
    window.removeEventListener("keyup",   this._onKeyUp);
    if (this.buffer.longPressTimer) clearTimeout(this.buffer.longPressTimer);
  }
}
